import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DynamicModuleGenerator } from '../dynamic-module-generator/dynamic-module-generator';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { getImageUrl } from '@/utils/images';
import { useLocalization } from '@/hooks/use-localization';
import { getTailwindColor } from '@/utils/colors';
import { MetaTags } from './components/meta-tags';
import { ContactMe } from '../contact-me/contact-me';
import { Banner } from '../banner/banner';
import { useContactMeForm } from '@/hooks/use-contact-me-form';

export const Page = () => {
    const { localize } = useLocalization();
    const { pageQuery } = useGlobalQueries();
    const { contactMeForm, setContactMeForm } = useContactMeForm();
    const containerRef = useRef<HTMLDivElement>(null);
    const [hideBanner, setHideBanner] = useState(false);

    const style: React.CSSProperties = {};
    if (pageQuery.data?.featured_image?.src) {
        style.backgroundImage = `url('${getImageUrl(pageQuery.data.featured_image.src, 'AUTOx1000')}')`;
    }

    const contactMeProps = useMemo(() => {
        if (!pageQuery.data?.contact_me_form) {
            return null;
        }

        const { contact_me_form: form } = pageQuery.data;

        return {
            formId: form.form_id,
            fields: form.form_fields || [],
            formTitle: form.form_title,
            formSubTitle: form.form_subtitle,
            buttonText: form.button_text,
            submitButtonText: form.submit_form_text,
            thankYouText: form.thank_you_text,
            gtm: form.form_gtm,
            contactMethod: form.contact_method,
        };
    }, [pageQuery.data?.contact_me_form]);

    const showBanner = useMemo(() => {
        if (!pageQuery?.data?.banner_cta_link) {
            return false;
        }

        return !hideBanner;
    }, [pageQuery.data, hideBanner]);

    const showContactForm = useMemo(() => {
        if (!pageQuery?.data?.contact_me_form) {
            return false;
        }

        return !hideBanner;
    }, [pageQuery.data, hideBanner]);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        containerRef.current.querySelectorAll('a').forEach(link => {
            try {
                const url = new URL(link.href);
                const isExternal = window?.location?.hostname !== url.hostname;
                const opensInNewTab = link.target === '_blank';
                const announcements = [];

                if (isExternal && !link.classList.contains('no-icon')) {
                    link.classList.add('external-link');
                }

                if (isExternal && !link.innerHTML.includes(localize('external-link'))) {
                    announcements.push(localize('external-link'));
                }
                if (opensInNewTab && !link.innerHTML.includes(localize('opens-in-new-tab'))) {
                    announcements.push(localize('opens-in-new-tab'));
                }

                if (announcements.length > 0 && !link.querySelector('.sr-link-info')) {
                    const srSpan = document.createElement('span');
                    srSpan.className = 'r-sr-only sr-link-info';
                    srSpan.textContent = announcements.join(', ');
                    link.appendChild(srSpan);
                }
            } catch {
                // Ignore invalid URLs
            }
        });
    }, [containerRef, pageQuery.data]);

    return (
        <main
            id="main-content"
            ref={containerRef}
            data-component="page"
            className={clsx('r-relative r-min-h-[555px]', getTailwindColor(pageQuery.data?.page_background_color, 'bg'))}
        >
            {pageQuery.isError || !pageQuery.data ? (
                <div className="r-container r-p-8">
                    <p className="r-bg-danger r-p-4 r-text-white">{localize('error-generic')}</p>
                </div>
            ) : (
                <>
                    <MetaTags />
                    {showContactForm ? (
                        <div style={style} className="r-absolute r-top-0 r-h-[330px] r-w-full r-bg-cover r-bg-center r-bg-no-repeat md:r-h-[450px]" />
                    ) : null}
                    {showBanner ? (
                        <Banner
                            title={pageQuery.data.banner_title}
                            text={pageQuery.data.banner_cta_text}
                            link={pageQuery.data.banner_cta_link}
                            onDismiss={() => setHideBanner(true)}
                        />
                    ) : null}
                    {contactMeForm.showContactBanner && contactMeProps ? (
                        <ContactMe {...contactMeProps} onDismiss={() => setContactMeForm({ showContactBanner: false })} />
                    ) : null}
                    <div className="r-z-10">
                        <DynamicModuleGenerator blocks={pageQuery.data?.blocks} isPageLevel />
                    </div>
                </>
            )}
        </main>
    );
};
